/**
 * VideoQoeController — the view state machine and metric emitter for one play
 * attempt. Pure and DOM-free (only `now()` and `trackEvent()` seams), so it is
 * unit-testable under the module's node jest env, mirroring PlayMetricsController.
 *
 * One `view_id` spans the whole attempt and PERSISTS across RT→LL failover, LL
 * retries, and player reinit — mode is an attribute, the switch is an event.
 *
 * State machine:
 *   idle → starting →(first frame)→ playing ⇄ stalled / paused → ended
 * Fatal before first frame → VSF; after → VPF. Leaving during `starting`
 * without a fatal → EBVS.
 */

import type { ContextAttributes } from '../core/events';
import { trackFrontendEvent } from '../core/events';
import {
  MAX_ERROR_EVENTS,
  MAX_QUALITY_CHANGE_EVENTS,
  MAX_REBUFFER_EVENTS,
  type StallCause,
  VIDEO_ERROR_EVENT,
  VIDEO_FIRST_FRAME_EVENT,
  VIDEO_HEARTBEAT_EVENT,
  VIDEO_MODE_SWITCH_EVENT,
  VIDEO_PLAYBACK_FAILURE_EVENT,
  VIDEO_QUALITY_CHANGE_EVENT,
  VIDEO_REBUFFER_EVENT,
  VIDEO_SESSION_END_EVENT,
  VIDEO_SESSION_START_EVENT,
  VIDEO_START_FAILURE_EVENT,
  type VideoMode,
  type VideoQoeGauges,
  type VideoQoeSignal,
  type ViewEndReason,
} from './types';

type TrackEvent = (name: string, attributes?: ContextAttributes, domain?: string) => void;

type State = 'idle' | 'starting' | 'playing' | 'stalled' | 'paused' | 'ended';

interface VideoQoeContext {
  channelId: string;
  bjId: string;
  hasRT: boolean;
  streamType?: string;
}

interface VideoQoeControllerOptions {
  context: VideoQoeContext;
  initialMode: VideoMode;
  now?: () => number;
  trackEvent?: TrackEvent;
  viewId?: string;
}

const round = (n: number) => Math.round(n);
const kbps = (bps: number) => Math.round(bps / 1000);
const sToMs = (s: number) => Math.round(s * 1000);

const fallbackViewId = (seed: number) => `view-${seed.toString(36)}-${(seed % 100000).toString(36)}`;

export class VideoQoeController {
  private readonly now: () => number;
  private readonly trackEvent: TrackEvent;
  private readonly context: VideoQoeContext;
  private readonly viewId: string;

  private state: State = 'idle';
  private mode: VideoMode;
  private lastAt: number;
  private t0 = 0;
  private firstFrameAt: number | null = null;
  private visible = true;
  private ivsSessionId?: string;
  private disposed = false;

  // View totals.
  private totalPlayingMs = 0;
  private totalStalledMs = 0;
  private totalPausedMs = 0;
  private llWatchMs = 0;
  private rtWatchMs = 0;
  private stallCount = 0;
  private errorCount = 0;
  private qualityUpCount = 0;
  private qualityDownCount = 0;
  private failoverCount = 0;
  private bitrateIntegral = 0; // kbps·ms
  private bitrateIntegralMs = 0;

  // Current-window (per-heartbeat) counters.
  private windowStartedAt: number;
  private winPlayingMs = 0;
  private winStalledMs = 0;
  private winPausedMs = 0;
  private winStallCount = 0;

  // Rolling gauge/derived state.
  private currentBitrateKbps?: number;
  private currentHeight?: number;
  private stallStartedAt = 0;
  private stallCause: StallCause = 'underrun';
  private autoplayBlocked = false;
  private capped = false;
  private terminal = false;
  private pendingEndReason: ViewEndReason | null = null;
  private pendingFailoverReason: string | null = null;
  private pendingLayerReason: 'selected' | 'unavailable' | null = null;
  private lastErrorType?: string;
  private lastErrorCode?: number;
  private retryAttempt = 0;

  // Heartbeat delta baselines.
  private prevFrames?: { dropped: number; total: number };
  private prevRt?: {
    bytesReceived?: number;
    packetsReceived?: number;
    packetsLost?: number;
    nackCount?: number;
    framesDecoded?: number;
    framesDropped?: number;
    freezeCount?: number;
    totalFreezesDuration?: number;
    jitterBufferDelay?: number;
    jitterBufferEmittedCount?: number;
  };

  private rebufferEmitted = 0;
  private errorEmitted = 0;
  private qualityChangeEmitted = 0;

  constructor({
    context,
    initialMode,
    now = () => Date.now(),
    trackEvent = trackFrontendEvent,
    viewId,
  }: VideoQoeControllerOptions) {
    this.context = context;
    this.mode = initialMode;
    this.now = now;
    this.trackEvent = trackEvent;
    const start = this.now();
    this.lastAt = start;
    this.windowStartedAt = start;
    this.viewId = viewId ?? this.makeViewId(start);
  }

  private makeViewId(seed: number): string {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch {
      // fall through
    }
    return fallbackViewId(seed);
  }

  private common(): ContextAttributes {
    return {
      view_id: this.viewId,
      mode: this.mode,
      channel_id: this.context.channelId,
      bj_id: this.context.bjId,
      ivs_session_id: this.ivsSessionId,
    };
  }

  /** Fold time since the last transition into the bucket for the current state. */
  private accrue(): void {
    const now = this.now();
    const elapsed = Math.max(0, now - this.lastAt);
    this.lastAt = now;
    if (this.state === 'playing') {
      this.totalPlayingMs += elapsed;
      this.winPlayingMs += elapsed;
      if (this.mode === 'll') this.llWatchMs += elapsed;
      else this.rtWatchMs += elapsed;
      if (this.currentBitrateKbps != null) {
        this.bitrateIntegral += this.currentBitrateKbps * elapsed;
        this.bitrateIntegralMs += elapsed;
      }
    } else if (this.state === 'stalled') {
      this.totalStalledMs += elapsed;
      this.winStalledMs += elapsed;
    } else if (this.state === 'paused') {
      this.totalPausedMs += elapsed;
      this.winPausedMs += elapsed;
    }
  }

  startView(): void {
    if (this.state !== 'idle') return;
    this.t0 = this.now();
    this.lastAt = this.t0;
    this.state = 'starting';
    this.trackEvent(VIDEO_SESSION_START_EVENT, {
      ...this.common(),
      has_rt: this.context.hasRT,
      stream_type: this.context.streamType,
    });
  }

  noteFirstFrame(source: 'rvfc' | 'playing'): void {
    if (this.state !== 'starting' || this.firstFrameAt != null) return;
    this.accrue();
    this.firstFrameAt = this.now();
    this.state = 'playing';
    this.trackEvent(VIDEO_FIRST_FRAME_EVENT, {
      ...this.common(),
      vst_ms: round(this.firstFrameAt - this.t0),
      source,
      autoplay_blocked: this.autoplayBlocked,
    });
  }

  notePlaying(): void {
    if (this.state === 'stalled') this.endStall(false);
    else if (this.state === 'paused') {
      this.accrue();
      this.state = 'playing';
    }
  }

  noteWaiting(): void {
    if (this.state === 'playing') this.beginStall('underrun');
  }

  notePause(): void {
    if (this.state === 'playing' || this.state === 'stalled') {
      this.accrue();
      this.state = 'paused';
    }
  }

  noteVisibility(visible: boolean): void {
    this.visible = visible;
  }

  noteModeChange(mode: VideoMode): void {
    if (mode === this.mode || this.state === 'ended') return;
    const trigger = this.pendingFailoverReason ? 'failover' : 'manual';
    this.trackEvent(VIDEO_MODE_SWITCH_EVENT, {
      ...this.common(),
      from_mode: this.mode,
      to_mode: mode,
      trigger,
      reason: this.pendingFailoverReason ?? 'user',
      pre_first_frame: this.firstFrameAt == null,
    });
    if (trigger === 'failover') this.failoverCount += 1;
    this.pendingFailoverReason = null;
    // A mid-playback switch replaces the element — treat as a stall until the
    // new element paints. During startup the VST clock simply keeps running.
    if (this.state === 'playing') this.beginStall('mode_switch');
    this.mode = mode;
  }

  /** The LL MediaPlayer instance was replaced (error reinit path). */
  noteLLInstanceChanged(): void {
    if (this.state === 'playing') this.beginStall('player_reinit');
  }

  noteQuality(bitrateBps: number, height: number, initiator: 'auto' | 'manual'): void {
    // Attribute the elapsed playing time to the OLD bitrate before switching.
    this.accrue();
    const toKbps = kbps(bitrateBps);
    if (this.currentBitrateKbps == null) {
      this.currentBitrateKbps = toKbps;
      this.currentHeight = height;
      return;
    }
    if (toKbps === this.currentBitrateKbps && height === this.currentHeight) return;
    const fromKbps = this.currentBitrateKbps;
    const direction = toKbps >= fromKbps ? 'up' : 'down';
    if (direction === 'up') this.qualityUpCount += 1;
    else this.qualityDownCount += 1;
    if (this.qualityChangeEmitted < MAX_QUALITY_CHANGE_EVENTS) {
      this.qualityChangeEmitted += 1;
      this.trackEvent(VIDEO_QUALITY_CHANGE_EVENT, {
        ...this.common(),
        from_bitrate_kbps: fromKbps,
        to_bitrate_kbps: toKbps,
        from_height: this.currentHeight,
        to_height: height,
        direction,
        initiator,
        reason: this.pendingLayerReason ?? 'none',
      });
    } else {
      this.capped = true;
    }
    this.currentBitrateKbps = toKbps;
    this.currentHeight = height;
    this.pendingLayerReason = null;
  }

  setIvsSessionId(id: string): void {
    if (!this.ivsSessionId) this.ivsSessionId = id;
  }

  noteSignal(signal: VideoQoeSignal): void {
    switch (signal.kind) {
      case 'll_error':
        this.lastErrorType = `ll_${signal.errorType}`;
        this.lastErrorCode = signal.errorCode;
        this.emitError('ll_player', this.lastErrorType, signal.errorCode, 'recoverable', signal.message);
        break;
      case 'rt_stage_error':
        this.lastErrorType = `rt_${signal.code}`;
        this.lastErrorCode = signal.code;
        if (signal.classification !== 'ignored') {
          this.emitError('rt_stage', this.lastErrorType, signal.code, signal.classification);
        }
        break;
      case 'll_retry':
      case 'rt_retry':
        this.retryAttempt = signal.attempt;
        break;
      case 'll_fatal':
      case 'rt_fatal_no_ll':
        this.emitFailure();
        this.terminal = true;
        this.pendingEndReason = 'fatal_error';
        break;
      case 'rt_failover':
        this.pendingFailoverReason = signal.reason;
        break;
      case 'broadcast_ended':
        this.pendingEndReason = 'broadcast_ended';
        break;
      case 'autoplay_blocked':
        this.autoplayBlocked = true;
        break;
      case 'rt_layer_selected':
        this.pendingLayerReason = signal.reason;
        break;
    }
  }

  recordHeartbeat(gauges: VideoQoeGauges): void {
    if (this.state === 'ended') return;
    this.accrue();
    if (gauges.bitrateBps != null) this.currentBitrateKbps = kbps(gauges.bitrateBps);
    this.emitHeartbeat(gauges);
    this.resetWindow();
  }

  endView(reason: ViewEndReason): void {
    if (this.state === 'ended') return;
    this.accrue();
    if (this.state === 'stalled') this.endStall(true);
    // Final window flush (timings only — no live gauges available at teardown).
    this.emitHeartbeat({});
    this.resetWindow();
    const finalReason = this.pendingEndReason ?? reason;
    const watchMs = this.totalPlayingMs + this.totalStalledMs + this.totalPausedMs;
    const denom = this.totalPlayingMs + this.totalStalledMs;
    this.trackEvent(VIDEO_SESSION_END_EVENT, {
      ...this.common(),
      end_reason: finalReason,
      watch_ms: round(watchMs),
      playing_ms: round(this.totalPlayingMs),
      stalled_ms: round(this.totalStalledMs),
      paused_ms: round(this.totalPausedMs),
      stall_count: this.stallCount,
      rebuffer_ratio: denom > 0 ? Number((this.totalStalledMs / denom).toFixed(4)) : 0,
      avg_bitrate_kbps: this.bitrateIntegralMs > 0 ? round(this.bitrateIntegral / this.bitrateIntegralMs) : undefined,
      quality_up_count: this.qualityUpCount,
      quality_down_count: this.qualityDownCount,
      ll_watch_ms: round(this.llWatchMs),
      rt_watch_ms: round(this.rtWatchMs),
      failover_count: this.failoverCount,
      error_count: this.errorCount,
      first_frame: this.firstFrameAt != null,
      vst_ms: this.firstFrameAt != null ? round(this.firstFrameAt - this.t0) : undefined,
      ebvs: this.firstFrameAt == null && !this.terminal,
      autoplay_blocked: this.autoplayBlocked,
      capped: this.capped,
    });
    this.state = 'ended';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.state !== 'ended') this.endView('route_change');
  }

  // ─── internals ─────────────────────────────────────────────────────────

  private beginStall(cause: StallCause): void {
    this.accrue();
    this.state = 'stalled';
    this.stallStartedAt = this.now();
    this.stallCause = cause;
    this.stallCount += 1;
    this.winStallCount += 1;
  }

  private endStall(truncated: boolean): void {
    this.accrue();
    const duration = round(this.now() - this.stallStartedAt);
    if (this.rebufferEmitted < MAX_REBUFFER_EVENTS) {
      this.rebufferEmitted += 1;
      this.trackEvent(VIDEO_REBUFFER_EVENT, {
        ...this.common(),
        duration_ms: duration,
        cause: this.stallCause,
        background: !this.visible,
        truncated,
      });
    } else {
      this.capped = true;
    }
    if (!truncated) this.state = 'playing';
  }

  private emitError(
    source: 'll_player' | 'rt_stage' | 'rt_video',
    errorType: string,
    errorCode: number,
    classification: 'recoverable' | 'fatal' | 'ignored',
    message?: string,
  ): void {
    this.errorCount += 1;
    if (this.errorEmitted >= MAX_ERROR_EVENTS) {
      this.capped = true;
      return;
    }
    this.errorEmitted += 1;
    this.trackEvent(VIDEO_ERROR_EVENT, {
      ...this.common(),
      source,
      error_type: errorType,
      error_code: errorCode,
      classification,
      retry_attempt: this.retryAttempt,
      phase: this.firstFrameAt == null ? 'startup' : 'playing',
      message,
    });
  }

  private emitFailure(): void {
    const name = this.firstFrameAt == null ? VIDEO_START_FAILURE_EVENT : VIDEO_PLAYBACK_FAILURE_EVENT;
    this.trackEvent(name, {
      ...this.common(),
      error_type: this.lastErrorType,
      error_code: this.lastErrorCode,
      attempts_used: this.retryAttempt,
    });
  }

  private emitHeartbeat(gauges: VideoQoeGauges): void {
    const now = this.now();
    const attrs: ContextAttributes = {
      ...this.common(),
      window_ms: round(now - this.windowStartedAt),
      playing_ms: round(this.winPlayingMs),
      stalled_ms: round(this.winStalledMs),
      paused_ms: round(this.winPausedMs),
      stall_count: this.winStallCount,
      visible: this.visible,
      bitrate_kbps: this.currentBitrateKbps,
      ll_latency_ms: gauges.llLatencySec != null ? sToMs(gauges.llLatencySec) : undefined,
      ll_buffer_ms: gauges.llBufferSec != null ? sToMs(gauges.llBufferSec) : undefined,
    };

    if (gauges.droppedFrames != null && gauges.totalFrames != null) {
      if (this.prevFrames) {
        attrs.dropped_frames = Math.max(0, round(gauges.droppedFrames - this.prevFrames.dropped));
        attrs.total_frames = Math.max(0, round(gauges.totalFrames - this.prevFrames.total));
      }
      this.prevFrames = { dropped: gauges.droppedFrames, total: gauges.totalFrames };
    }

    if (gauges.rt) {
      const rt = gauges.rt;
      if (rt.networkQuality != null) attrs.rt_network_quality = rt.networkQuality;
      if (rt.jitter != null) attrs.rt_jitter_ms = sToMs(rt.jitter);
      const prev = this.prevRt;
      if (prev) {
        const d = (cur?: number, was?: number) =>
          cur != null && was != null ? Math.max(0, round(cur - was)) : undefined;
        attrs.rt_bytes_received = d(rt.bytesReceived, prev.bytesReceived);
        attrs.rt_packets_received = d(rt.packetsReceived, prev.packetsReceived);
        attrs.rt_packets_lost = d(rt.packetsLost, prev.packetsLost);
        attrs.rt_nack_count = d(rt.nackCount, prev.nackCount);
        attrs.rt_frames_decoded = d(rt.framesDecoded, prev.framesDecoded);
        attrs.rt_frames_dropped = d(rt.framesDropped, prev.framesDropped);
        attrs.rt_freeze_count = d(rt.freezeCount, prev.freezeCount);
        if (rt.totalFreezesDuration != null && prev.totalFreezesDuration != null) {
          attrs.rt_freeze_ms = sToMs(Math.max(0, rt.totalFreezesDuration - prev.totalFreezesDuration));
        }
        if (
          rt.jitterBufferDelay != null &&
          prev.jitterBufferDelay != null &&
          rt.jitterBufferEmittedCount != null &&
          prev.jitterBufferEmittedCount != null
        ) {
          const emitted = rt.jitterBufferEmittedCount - prev.jitterBufferEmittedCount;
          if (emitted > 0) attrs.rt_jitter_buffer_ms = sToMs((rt.jitterBufferDelay - prev.jitterBufferDelay) / emitted);
        }
      }
      this.prevRt = {
        bytesReceived: rt.bytesReceived,
        packetsReceived: rt.packetsReceived,
        packetsLost: rt.packetsLost,
        nackCount: rt.nackCount,
        framesDecoded: rt.framesDecoded,
        framesDropped: rt.framesDropped,
        freezeCount: rt.freezeCount,
        totalFreezesDuration: rt.totalFreezesDuration,
        jitterBufferDelay: rt.jitterBufferDelay,
        jitterBufferEmittedCount: rt.jitterBufferEmittedCount,
      };
    }

    this.trackEvent(VIDEO_HEARTBEAT_EVENT, attrs);
  }

  private resetWindow(): void {
    this.windowStartedAt = this.now();
    this.winPlayingMs = 0;
    this.winStalledMs = 0;
    this.winPausedMs = 0;
    this.winStallCount = 0;
  }
}
